import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getOtpLogs,
  getOtpLogById,
  getOtpLogsByUserId,
  getOtpAnalytics,
  getOtpAnalyticsOverview,
  getOtpStats,
  getOtpDeliveryRate,
  getOtpSuccessRate,
  getOtpChannelBreakdown,
  resendOtp,
} from "../services/get-otp-logs";
import { OtpLogFilters } from "@/pages/otp-log/types/otp-log";
import { toast } from "@/hooks/use-toast";

export const useOtpLogs = (filters: OtpLogFilters) => {
  return useQuery({
    queryKey: ["otp-logs", filters],
    queryFn: () => getOtpLogs(filters),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

export const useOtpLog = (id: string) => {
  return useQuery({
    queryKey: ["otp-log", id],
    queryFn: () => getOtpLogById(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 10, // 10 minutes
    refetchOnWindowFocus: false,
  });
};

export const useOtpLogsByUserId = (userId: string) => {
  return useQuery({
    queryKey: ["otp-logs", "user", userId],
    queryFn: () => getOtpLogsByUserId(userId),
    enabled: !!userId,
    staleTime: 1000 * 60 * 10, // 10 minutes
    refetchOnWindowFocus: false,
  });
};

export const useOtpAnalytics = (filters?: {
  startDate?: string;
  endDate?: string;
}) => {
  return useQuery({
    queryKey: ["otp-analytics", filters],
    queryFn: () => getOtpAnalytics(filters),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

export const useOtpAnalyticsOverview = (filters?: {
  startDate?: string;
  endDate?: string;
}) => {
  return useQuery({
    queryKey: ["otp-analytics-overview", filters],
    queryFn: () => getOtpAnalyticsOverview(filters),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

export const useOtpStats = (filters?: {
  startDate?: string;
  endDate?: string;
}) => {
  return useQuery({
    queryKey: ["otp-stats", filters],
    queryFn: () => getOtpStats(filters),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

export const useOtpDeliveryRate = (filters?: {
  startDate?: string;
  endDate?: string;
}) => {
  return useQuery({
    queryKey: ["otp-delivery-rate", filters],
    queryFn: () => getOtpDeliveryRate(filters),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

export const useOtpSuccessRate = (filters?: {
  startDate?: string;
  endDate?: string;
}) => {
  return useQuery({
    queryKey: ["otp-success-rate", filters],
    queryFn: () => getOtpSuccessRate(filters),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

export const useOtpChannelBreakdown = (filters?: {
  startDate?: string;
  endDate?: string;
}) => {
  return useQuery({
    queryKey: ["otp-channel-breakdown", filters],
    queryFn: () => getOtpChannelBreakdown(filters),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

export const useResendOtp = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (logId: string) => resendOtp(logId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["otp-logs"] });
      toast({
        title: "Success",
        description: "OTP resent successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to resend OTP",
        variant: "destructive",
      });
    },
  });
};

